import React from "react";
import { Box, Typography, Tooltip, Popover } from "@mui/material";
import {
  PhoneIncoming,
  FilePlus2,
  Send,
  CircleX,
  BadgeCheck,
  Truck,
} from "lucide-react";
import { PipelineStageCard } from "./PipelineStageCard";
import { SalesPipelineData, SalesPipelineStageId } from "./types";
import { DashboardSectionDivider, useActivateDashboardSection } from "../DashboardSectionDivider";

interface SalesPipelineOverviewProps {
  data: SalesPipelineData;
  title?: string;
  subtitle?: string;
  onStageClick?: (stageId: SalesPipelineStageId) => void;
}

const STAGE_ICONS: Record<SalesPipelineStageId, React.ReactNode> = {
  enquiry_received: <PhoneIncoming size={14} strokeWidth={2} />,
  quotation_created: <FilePlus2 size={14} strokeWidth={2} />,
  quotation_won: <Send size={14} strokeWidth={2} />,
  quotation_lost: <CircleX size={14} strokeWidth={2} />,
  order_own: <BadgeCheck size={14} strokeWidth={2} />,
  dispatched: <Truck size={14} strokeWidth={2} />,
};

const STAGE_ANIM_INDEX: Record<SalesPipelineStageId, number> = {
  enquiry_received: 0,
  quotation_created: 1,
  quotation_won: 2,
  quotation_lost: 3,
  order_own: 4,
  dispatched: 5,
};

const FLOW = {
  active: "#d2363a",
  activeSoft: "#f0a0a2",
  won: "#0f9f6e",
  wonSoft: "#8fd4bb",
  lost: "#c2410c",
  lostSoft: "#e8a57a",
  base: "#cbd5e1",
};

const flowLineSx = {
  "@keyframes pipelineFlowDraw": {
    from: { strokeDashoffset: 160 },
    to: { strokeDashoffset: 0 },
  },
  "& .pipeline-flow-line": {
    strokeDasharray: 160,
    strokeDashoffset: 0,
    animation: "pipelineFlowDraw 0.7s ease-out 0.12s both",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .pipeline-flow-line": {
      animation: "none",
      strokeDasharray: "none",
    },
  },
};

type FlowDotInfo = {
  title: string;
  detail: string;
  relatedStages: SalesPipelineStageId[];
};

const ArrowHead: React.FC<{ color: string; x: number; y: number }> = ({
  color,
  x,
  y,
}) => (
  <path
    d={`M${x - 5} ${y - 4.5} L${x + 1} ${y} L${x - 5} ${y + 4.5}`}
    fill="none"
    stroke={color}
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    vectorEffect="non-scaling-stroke"
  />
);

const FlowDot: React.FC<{
  color: string;
  top?: string;
  left?: string;
  info: FlowDotInfo;
  onActivate: (stages: SalesPipelineStageId[] | null) => void;
}> = ({ color, top = "50%", left = "50%", info, onActivate }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onActivate(info.relatedStages);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onActivate(null);
  };

  return (
    <>
      <Tooltip
        title={info.title}
        arrow
        placement="top"
        enterDelay={120}
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: "'Poppins', sans-serif",
              fontSize: "11px",
              fontWeight: 500,
              backgroundColor: "#111827",
            },
          },
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label={info.title}
          onMouseEnter={() => onActivate(info.relatedStages)}
          onMouseLeave={() => {
            if (!open) onActivate(null);
          }}
          onClick={handleOpen}
          sx={{
            position: "absolute",
            top,
            left,
            width: 12,
            height: 12,
            p: 0,
            borderRadius: "50%",
            backgroundColor: "#fff",
            border: `2px solid ${color}`,
            transform: "translate(-50%, -50%)",
            boxShadow: `0 0 0 3px ${color}24`,
            zIndex: 2,
            cursor: "pointer",
            transition: "transform 0.18s ease, box-shadow 0.18s ease",
            "&:hover": {
              transform: "translate(-50%, -50%) scale(1.35)",
              boxShadow: `0 0 0 5px ${color}33`,
            },
            "&:focus-visible": {
              outline: `2px solid ${color}`,
              outlineOffset: 2,
            },
          }}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              px: 1.5,
              py: 1.25,
              maxWidth: 240,
              borderRadius: "10px",
              border: `1px solid ${color}55`,
              boxShadow: "0 10px 28px rgba(17,24,39,0.12)",
            },
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            color: "#111827",
            mb: 0.4,
          }}
        >
          {info.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "11px",
            color: "#6b7280",
            lineHeight: 1.45,
          }}
        >
          {info.detail}
        </Typography>
      </Popover>
    </>
  );
};

interface ConnectorProps {
  variant?: "straight" | "fork-in" | "fork-out";
  onActivate?: (stages: SalesPipelineStageId[] | null) => void;
  straightInfo?: FlowDotInfo;
}

const Connector: React.FC<ConnectorProps> = ({
  variant = "straight",
  onActivate = () => undefined,
  straightInfo,
}) => {
  const uid = React.useId().replace(/:/g, "");

  if (variant === "fork-in") {
    return (
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          width: 32,
          flexShrink: 0,
          alignSelf: "stretch",
          position: "relative",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 56 200"
          preserveAspectRatio="none"
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            inset: 0,
            ...flowLineSx,
          }}
        >
          <defs>
            <linearGradient id={`forkInWon-${uid}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={FLOW.activeSoft} />
              <stop offset="100%" stopColor={FLOW.won} />
            </linearGradient>
            <linearGradient id={`forkInLost-${uid}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={FLOW.activeSoft} />
              <stop offset="100%" stopColor={FLOW.lost} />
            </linearGradient>
          </defs>

          <path
            d="M0 100 H18"
            fill="none"
            stroke={FLOW.base}
            strokeWidth="5"
            strokeLinecap="round"
            opacity="0.3"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M18 100 C18 55, 26 38, 52 32"
            fill="none"
            stroke={FLOW.base}
            strokeWidth="5"
            strokeLinecap="round"
            opacity="0.28"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M18 100 C18 145, 26 162, 52 168"
            fill="none"
            stroke={FLOW.base}
            strokeWidth="5"
            strokeLinecap="round"
            opacity="0.28"
            vectorEffect="non-scaling-stroke"
          />

          <path
            className="pipeline-flow-line"
            d="M0 100 H18"
            fill="none"
            stroke={FLOW.active}
            strokeWidth="2.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            className="pipeline-flow-line"
            d="M18 100 C18 55, 26 38, 52 32"
            fill="none"
            stroke={`url(#forkInWon-${uid})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            className="pipeline-flow-line"
            d="M18 100 C18 145, 26 162, 52 168"
            fill="none"
            stroke={`url(#forkInLost-${uid})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          <ArrowHead color={FLOW.won} x={54} y={32} />
          <ArrowHead color={FLOW.lost} x={54} y={168} />
        </Box>
        <FlowDot
          color={FLOW.active}
          top="50%"
          left="32%"
          onActivate={onActivate}
          info={{
            title: "Decision point",
            detail:
              "From Quotation Created the flow splits into Quotation Won (won path) or Quotation Lost (end).",
            relatedStages: [
              "quotation_created",
              "quotation_won",
              "quotation_lost",
            ],
          }}
        />
      </Box>
    );
  }

  if (variant === "fork-out") {
    return (
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          width: 32,
          flexShrink: 0,
          alignSelf: "stretch",
          position: "relative",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 56 200"
          preserveAspectRatio="none"
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            inset: 0,
            ...flowLineSx,
          }}
        >
          <defs>
            <linearGradient id={`forkOutWon-${uid}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={FLOW.won} />
              <stop offset="100%" stopColor={FLOW.active} />
            </linearGradient>
          </defs>

          <path
            d="M0 168 H16"
            fill="none"
            stroke={FLOW.lostSoft}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="3.5 4.5"
            vectorEffect="non-scaling-stroke"
          />

          <path
            d="M0 32 C20 32, 24 32, 24 58 C24 92, 24 100, 44 100"
            fill="none"
            stroke={FLOW.base}
            strokeWidth="5"
            strokeLinecap="round"
            opacity="0.28"
            vectorEffect="non-scaling-stroke"
          />
          <path
            className="pipeline-flow-line"
            d="M0 32 C20 32, 24 32, 24 58 C24 92, 24 100, 44 100"
            fill="none"
            stroke={`url(#forkOutWon-${uid})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          <ArrowHead color={FLOW.active} x={52} y={100} />
        </Box>
        <FlowDot
          color={FLOW.lostSoft}
          top="84%"
          left="32%"
          onActivate={onActivate}
          info={{
            title: "Lost path ends",
            detail:
              "Quotation Lost is a terminal stage. These enquiries do not continue to Order Own or Dispatched.",
            relatedStages: ["quotation_lost"],
          }}
        />
        <FlowDot
          color={FLOW.won}
          top="29%"
          left="43%"
          onActivate={onActivate}
          info={{
            title: "Won path continues",
            detail:
              "Quotation Won continues into Order Own, then Dispatched.",
            relatedStages: ["quotation_won", "order_own", "dispatched"],
          }}
        />
      </Box>
    );
  }

  const info: FlowDotInfo = straightInfo || {
    title: "Stage continues",
    detail: "Flow moves to the next stage in the sales pipeline.",
    relatedStages: [],
  };

  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        flexShrink: 0,
        position: "relative",
        px: 0,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 48 24"
        sx={{ width: 24, height: 16, overflow: "visible", ...flowLineSx }}
      >
        <defs>
          <linearGradient id={`straightFlow-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={FLOW.activeSoft} />
            <stop offset="100%" stopColor={FLOW.active} />
          </linearGradient>
        </defs>
        <path
          d="M4 12 H34"
          fill="none"
          stroke={FLOW.base}
          strokeWidth="5"
          strokeLinecap="round"
          opacity="0.35"
        />
        <path
          className="pipeline-flow-line"
          d="M4 12 H34"
          fill="none"
          stroke={`url(#straightFlow-${uid})`}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <ArrowHead color={FLOW.active} x={42} y={12} />
      </Box>
      <FlowDot
        color={FLOW.active}
        top="50%"
        left="10%"
        onActivate={onActivate}
        info={info}
      />
    </Box>
  );
};

const MobileConnector: React.FC = () => (
  <Box
    sx={{
      display: { xs: "flex", md: "none" },
      flexDirection: "column",
      alignItems: "center",
      py: 1,
      gap: 0.4,
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        border: `2px solid ${FLOW.active}`,
        backgroundColor: "#fff",
        boxShadow: `0 0 0 3px ${FLOW.active}22`,
      }}
    />
    <Box
      sx={{
        width: 2.5,
        height: 18,
        borderRadius: 999,
        background: `linear-gradient(180deg, ${FLOW.activeSoft}, ${FLOW.active})`,
      }}
    />
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderTop: `6px solid ${FLOW.active}`,
      }}
    />
  </Box>
);

export const SalesPipelineOverview: React.FC<SalesPipelineOverviewProps> = ({
  data,
  title = "Sales Pipeline Overview",
  subtitle = "Enquiry to dispatch flow for the selected period · hover or click flow dots for path details",
  onStageClick,
}) => {
  const { activateSection } = useActivateDashboardSection();
  const [activeStages, setActiveStages] = React.useState<
    SalesPipelineStageId[] | null
  >(null);

  const byId = Object.fromEntries(
    data.stages.map((stage) => [stage.id, stage]),
  ) as Record<SalesPipelineStageId, (typeof data.stages)[number]>;

  const maxValue = Math.max(...data.stages.map((s) => s.value), 1);

  const getEmphasis = (id: SalesPipelineStageId) => {
    if (!activeStages || activeStages.length === 0) return "normal" as const;
    return activeStages.includes(id)
      ? ("highlight" as const)
      : ("dim" as const);
  };

  const renderStage = (id: SalesPipelineStageId) => (
    <PipelineStageCard
      stage={byId[id]}
      maxValue={maxValue}
      icon={STAGE_ICONS[id]}
      emphasis={getEmphasis(id)}
      animationIndex={STAGE_ANIM_INDEX[id]}
      onClick={onStageClick ? () => onStageClick(id) : undefined}
    />
  );

  return (
    <Box sx={{ mt: 2.75 }} onClickCapture={() => activateSection("pipeline")}>
      <DashboardSectionDivider sectionId="pipeline" />
      <Box
        sx={{
          mb: 1.75,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "12px",
              color: "#6b7280",
              mt: 0.25,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {[
            { label: "Active", color: "#d2363a" },
            { label: "Won path", color: "#0f9f6e" },
            { label: "Lost", color: "#c2410c" },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "10px",
                  fontWeight: 500,
                  color: "#6b7280",
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          borderRadius: "16px",
          border: "1.5px solid #d2363a",
          background:
            "radial-gradient(1200px 180px at 10% 0%, #fff5f5 0%, transparent 55%), radial-gradient(900px 160px at 90% 100%, #f3faf7 0%, transparent 50%), #ffffff",
          px: { xs: 1.25, md: 1.75 },
          py: { xs: 2, md: 3 },
          overflow: "visible",
          boxShadow:
            "0 2px 6px rgba(15, 23, 42, 0.04), 0 14px 32px rgba(15, 23, 42, 0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
          transformStyle: "preserve-3d",
          transition:
            "box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1), transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
          "@keyframes pipelinePanelIn": {
            from: {
              opacity: 0,
              transform: "translateY(10px) rotateX(4deg)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0) rotateX(0deg)",
            },
          },
          animation: "pipelinePanelIn 560ms cubic-bezier(0.22, 1, 0.36, 1) both",
          "&:hover": {
            boxShadow:
              "0 6px 14px rgba(15, 23, 42, 0.08), 0 18px 40px rgba(15, 23, 42, 0.1), 0 1px 0 rgba(255,255,255,0.95) inset",
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            width: "100%",
            minHeight: { md: 180 },
            gap: { xs: 0, md: 0.25 },
          }}
        >
          {renderStage("enquiry_received")}
          <Connector
            onActivate={setActiveStages}
            straightInfo={{
              title: "Enquiry → Quotation",
              detail: "Enquiry Received moves into Quotation Created.",
              relatedStages: ["enquiry_received", "quotation_created"],
            }}
          />
          <MobileConnector />

          {renderStage("quotation_created")}
          <Connector variant="fork-in" onActivate={setActiveStages} />
          <MobileConnector />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 2.5,
              py: { md: 0.5 },
              flex: { md: "1 1 0" },
              minWidth: { md: 0 },
              width: { xs: "100%", md: "auto" },
              "& > *": {
                width: "100%",
                flex: "none !important",
              },
            }}
          >
            {renderStage("quotation_won")}
            {renderStage("quotation_lost")}
          </Box>

          <Connector variant="fork-out" onActivate={setActiveStages} />
          <MobileConnector />

          {renderStage("order_own")}
          <Connector
            onActivate={setActiveStages}
            straightInfo={{
              title: "Order → Dispatch",
              detail: "Order Own continues to Dispatched.",
              relatedStages: ["order_own", "dispatched"],
            }}
          />
          <MobileConnector />

          {renderStage("dispatched")}
        </Box>
      </Box>
    </Box>
  );
};
